/**
 * Tablero de monitoreo de campo.
 *
 * Responde, en este orden, las preguntas que el coordinador se hace cada día:
 *  1. ¿Cuánto llevamos y cuánto falta? (§2)
 *  2. ¿Va parejo por estrato? (§4.2)
 *  3. ¿La muestra se está desviando de la composición del padrón? (§3)
 *  4. ¿Cómo va la tasa de respuesta? (§5.3.6)
 *  5. ¿Hay algo que auditar? (§9.4)
 *  6. ¿El ritmo diario alcanza para terminar en el calendario? (§9.1)
 */

import { useQuery } from '@tanstack/react-query';

import { api } from '../api/client';
import {
  BarChart,
  ColumnChart,
  DivergingChart,
  ProgressBar,
  StatusChip,
  TableView,
  Tile,
  fmtDecimal,
  fmtInt,
  fmtPct,
  fmtPp,
} from '../components/charts';

export function DashboardPage() {
  const overview = useQuery({ queryKey: ['monitoring', 'overview'], queryFn: api.monitoringOverview });
  const strata = useQuery({ queryKey: ['monitoring', 'strata'], queryFn: api.monitoringStrata });
  const deviation = useQuery({ queryKey: ['monitoring', 'deviation'], queryFn: api.targetDeviation });
  const rates = useQuery({ queryKey: ['monitoring', 'rates'], queryFn: api.responseRates });
  const flags = useQuery({ queryKey: ['quality', 'summary'], queryFn: api.flagSummary });
  const daily = useQuery({ queryKey: ['monitoring', 'daily'], queryFn: api.dailyProgress });
  const verification = useQuery({
    queryKey: ['quality', 'verification', 'progress'],
    queryFn: api.verificationProgress,
  });

  if (overview.isLoading) {
    return (
      <div className="center-screen">
        <div className="spinner" />
      </div>
    );
  }

  if (overview.isError || !overview.data) {
    return (
      <div className="callout callout--critical">
        <span className="callout__title">No se pudo cargar el monitoreo</span>
        {overview.error instanceof Error ? overview.error.message : 'Error desconocido.'}
      </div>
    );
  }

  const data = overview.data;
  const criticalFlags = flags.data?.filter((f) => f.severity === 'critical') ?? [];
  const openCritical = criticalFlags.reduce((a, f) => a + f.open, 0);

  return (
    <>
      <div className="page-head">
        <h1 className="page-title">Monitoreo de campo</h1>
        <p className="page-sub">
          {data.wave.label} · {data.wave.status.replace('_', ' ')}. Los conteos excluyen el piloto y
          las entrevistas anuladas.
        </p>
      </div>

      {/* ------------------------- Alertas inmediatas ------------------------- */}

      {openCritical > 0 ? (
        <div className="callout callout--critical">
          <span className="callout__title">
            <StatusChip status="critical">{openCritical} alerta(s) crítica(s) sin resolver</StatusChip>
          </span>
          {criticalFlags
            .filter((f) => f.open > 0)
            .map((f) => `${f.label} (${f.open})`)
            .join(' · ')}
        </div>
      ) : null}

      {deviation.data?.educationNeedsRaking ? (
        <div className="callout callout--warning">
          <span className="callout__title">
            <StatusChip status="warning">Escolaridad fuera de rango</StatusChip>
          </span>
          La desviación de escolaridad supera los ±{deviation.data.educationRakingThresholdPp} pp que
          §3.3 fija como umbral. Hay que incluir escolaridad en el raking de la ponderación (§6.3).
        </div>
      ) : null}

      {/* ---------------------------- Avance total ---------------------------- */}

      <div className="panel">
        <div className="panel__head">
          <div>
            <h2 className="panel__title">
              Avance del levantamiento <span className="ref">§2</span>
            </h2>
            <p className="panel__note">
              Se programan {fmtInt(data.progress.programmed)} entrevistas para obtener{' '}
              {fmtInt(data.progress.target)} efectivas, absorbiendo la merma de ~
              {fmtPct(data.counts.expectedAnnulmentRate, 0)} por control de calidad.
            </p>
          </div>
        </div>

        <div className="grid grid--4" style={{ marginBottom: 20 }}>
          <Tile
            label="Entrevistas efectivas"
            value={fmtInt(data.counts.completed)}
            hero
            meta={`Faltan ${fmtInt(data.progress.remaining)} para la meta`}
          />
          <Tile
            label="Margen de error"
            reference="§2.1"
            value={`±${fmtDecimal(data.marginOfError.effective * 100)}`}
            unit="%"
            meta={`Teórico ±${fmtDecimal(data.marginOfError.theoretical * 100)}% · deff ${data.marginOfError.designEffect}`}
          />
          <Tile
            label="Duración promedio"
            reference="§2"
            value={
              data.duration.averageMinutes === null
                ? '—'
                : fmtDecimal(data.duration.averageMinutes)
            }
            unit="min"
            meta={`Diseño ${data.duration.designRange.min}–${data.duration.designRange.max} min · mínimo ${data.duration.minimumAllowed}`}
          />
          <Tile
            label="Tasa de respuesta"
            reference="AAPOR RR3"
            value={rates.data ? fmtPct(rates.data.overall.responseRateRR3, 1) : '—'}
            meta={
              rates.data
                ? `Rechazo ${fmtPct(rates.data.overall.refusalRateREF2, 1)} · contacto ${fmtPct(rates.data.overall.contactRateCON2, 1)}`
                : undefined
            }
          />
        </div>

        <ProgressBar
          value={data.counts.completed}
          target={data.progress.target}
          programmed={data.progress.programmed}
        />

        <div className="row row--wrap" style={{ marginTop: 16 }}>
          <StatusChip status="good">{fmtInt(data.counts.completed)} completas</StatusChip>
          {data.counts.partial > 0 ? (
            <StatusChip status="warning">{fmtInt(data.counts.partial)} parciales</StatusChip>
          ) : null}
          {data.counts.inProgress > 0 ? (
            <StatusChip status="neutral">{fmtInt(data.counts.inProgress)} en curso</StatusChip>
          ) : null}
          {data.counts.annulled > 0 ? (
            <StatusChip status="critical">
              {fmtInt(data.counts.annulled)} anuladas ({fmtPct(data.counts.annulmentRate, 1)})
            </StatusChip>
          ) : null}
          {data.counts.pilot > 0 ? (
            <StatusChip status="neutral">{fmtInt(data.counts.pilot)} del piloto</StatusChip>
          ) : null}
        </div>

        <details className="note">
          <summary>Sobre los dos márgenes de error</summary>
          <p>{data.marginOfError.note}</p>
        </details>
      </div>

      {/* --------------------------- Por estrato ----------------------------- */}

      <div className="grid grid--2">
        <div className="panel">
          <div className="panel__head">
            <div>
              <h2 className="panel__title">
                Avance por estrato <span className="ref">§4.2</span>
              </h2>
              <p className="panel__note">
                La línea gris marca la meta de cada estrato. Los estratos C y E sólo se leen como
                tendencia, nunca como resultado aislado.
              </p>
            </div>
          </div>

          {strata.data ? (
            <>
              <BarChart
                showTargetLegend
                rows={strata.data.map((stratum) => ({
                  key: stratum.id,
                  label: `${stratum.id} · ${stratum.name}`,
                  value: stratum.completed,
                  target: stratum.target,
                  valueLabel: `${fmtInt(stratum.completed)} / ${fmtInt(stratum.target)}`,
                  tooltip: `${stratum.name}: ${fmtInt(stratum.completed)} de ${fmtInt(stratum.target)} (${fmtPct(stratum.share, 1)}) · margen ±${fmtDecimal(stratum.marginOfError.effective * 100)}%`,
                }))}
              />
              <div className="row row--wrap" style={{ marginTop: 12 }}>
                {strata.data
                  .filter((s) => s.readAsTrendOnly)
                  .map((s) => (
                    <StatusChip key={s.id} status="warning">
                      Estrato {s.id}: sólo tendencia (±
                      {fmtDecimal(s.documentedMarginOfError * 100)}%)
                    </StatusChip>
                  ))}
              </div>
              <TableView
                columns={['Estrato', 'Meta', 'Levantadas', 'Faltan', 'Avance', 'Margen efectivo']}
                rows={strata.data.map((s) => [
                  `${s.id} · ${s.name}`,
                  fmtInt(s.target),
                  fmtInt(s.completed),
                  fmtInt(s.remaining),
                  fmtPct(s.share, 1),
                  `±${fmtDecimal(s.marginOfError.effective * 100)}%`,
                ])}
              />
            </>
          ) : (
            <div className="spinner" />
          )}
        </div>

        {/* ------------------------ Tasa de respuesta ----------------------- */}

        <div className="panel">
          <div className="panel__head">
            <div>
              <h2 className="panel__title">
                Tasa de respuesta por estrato <span className="ref">§5.3.6</span>
              </h2>
              <p className="panel__note">
                AAPOR RR3. §12 la pide como anexo desagregado por estrato.
              </p>
            </div>
          </div>

          {rates.data ? (
            <>
              <BarChart
                max={1}
                rows={rates.data.byStratum.map((stratum) => ({
                  key: stratum.stratumId,
                  label: `${stratum.stratumId} · ${stratum.name}`,
                  value: stratum.responseRateRR3,
                  valueLabel: fmtPct(stratum.responseRateRR3, 1),
                  tooltip: `${stratum.name}: RR3 ${fmtPct(stratum.responseRateRR3, 1)} · rechazo ${fmtPct(stratum.refusalRateREF2, 1)} · ${fmtInt(stratum.tally['interviewComplete'] ?? 0)} completas`,
                }))}
              />
              <TableView
                columns={['Estrato', 'Completas', 'Rechazos', 'Sin contacto', 'RR3', 'REF2']}
                rows={rates.data.byStratum.map((s) => [
                  `${s.stratumId} · ${s.name}`,
                  fmtInt(s.tally['interviewComplete'] ?? 0),
                  fmtInt(s.tally['refusal'] ?? 0),
                  fmtInt(s.tally['nonContact'] ?? 0),
                  fmtPct(s.responseRateRR3, 1),
                  fmtPct(s.refusalRateREF2, 1),
                ])}
              />
              <details className="note">
                <summary>Cómo se calcula</summary>
                <p>{rates.data.note}</p>
              </details>
            </>
          ) : (
            <div className="spinner" />
          )}
        </div>
      </div>

      {/* --------------------- Desviación vs metas de §3 --------------------- */}

      {deviation.data && deviation.data.total > 0 ? (
        <div className="panel">
          <div className="panel__head">
            <div>
              <h2 className="panel__title">
                Desviación respecto a la composición del padrón <span className="ref">§3</span>
              </h2>
              <p className="panel__note">
                <span className="strong">No son cuotas.</span> {deviation.data.correctionNote}
              </p>
            </div>
            <span className="chip chip--accent">base {fmtInt(deviation.data.total)}</span>
          </div>

          <div className="grid grid--2">
            <div>
              <p className="field__label">Por género</p>
              <DivergingChart
                rows={deviation.data.gender.map((row) => ({
                  key: row.code,
                  label: row.label,
                  value: row.deviationPp,
                  tooltip: `${row.label}: ${fmtInt(row.achieved)} de meta ${fmtInt(row.target)} · ${fmtPct(row.achievedShare, 1)} logrado contra ${fmtPct(row.padronShare, 1)} del padrón`,
                }))}
              />
            </div>

            <div>
              <p className="field__label">Por grupo de edad</p>
              <DivergingChart
                rows={deviation.data.ageGroups.map((row) => ({
                  key: row.code,
                  label: row.label,
                  value: row.deviationPp,
                  tooltip: `${row.label}: ${fmtInt(row.achieved)} de meta ${fmtInt(row.target)} · ${fmtPct(row.achievedShare, 1)} logrado contra ${fmtPct(row.padronShare, 1)} del padrón`,
                }))}
              />
            </div>
          </div>

          <div className="divider" />

          <p className="field__label">
            Por escolaridad <span className="ref">§3.3</span>
          </p>
          <DivergingChart
            rows={deviation.data.education.map((row) => ({
              key: row.code,
              label: row.label,
              value: row.deviationPp,
              tooltip: `${row.label}: ${fmtInt(row.achieved)} · ${fmtPct(row.achievedShare, 1)} contra ${fmtPct(row.padronShare, 1)} del Censo`,
            }))}
            maxAbs={Math.max(
              deviation.data.educationRakingThresholdPp * 1.5,
              ...deviation.data.education.map((r) => Math.abs(r.deviationPp)),
            )}
          />
          <p className="faint" style={{ marginTop: 8 }}>
            Las proporciones de escolaridad provienen del Censo INEGI 2020 y son aproximadas: el INE
            no captura escolaridad en la credencial. Umbral para incluirla en el raking: ±
            {deviation.data.educationRakingThresholdPp} pp.
          </p>

          <TableView
            caption="Desviación por categoría, en puntos porcentuales respecto al padrón."
            columns={['Categoría', 'Meta', 'Logradas', '% logrado', '% padrón', 'Desviación']}
            rows={[
              ...deviation.data.gender,
              ...deviation.data.ageGroups,
              ...deviation.data.education,
            ].map((row) => [
              row.label,
              fmtInt(row.target),
              fmtInt(row.achieved),
              fmtPct(row.achievedShare, 1),
              fmtPct(row.padronShare, 1),
              fmtPp(row.deviationPp),
            ])}
          />

          <div className="callout callout--info" style={{ marginTop: 16, marginBottom: 0 }}>
            <span className="callout__title">Cómo se corrige una desviación</span>
            Reprogramando visitas a horario vespertino y fin de semana a partir del día{' '}
            {deviation.data.rescheduleReviewDay} de campo, y al cierre por ponderación. Nunca
            sustituyendo a la persona seleccionada en la vivienda.
          </div>
        </div>
      ) : null}

      {/* --------------------------- Ritmo diario ---------------------------- */}

      {daily.data && daily.data.days.length > 0 ? (
        <div className="panel">
          <div className="panel__head">
            <div>
              <h2 className="panel__title">
                Entrevistas por día <span className="ref">§9.1</span>
              </h2>
              <p className="panel__note">
                La línea de referencia es la capacidad esperada del equipo:{' '}
                {daily.data.expectedPerInterviewerPerDay.urban.min}–
                {daily.data.expectedPerInterviewerPerDay.urban.max} entrevistas por encuestador y
                día en urbano, {daily.data.expectedPerInterviewerPerDay.rural.min}–
                {daily.data.expectedPerInterviewerPerDay.rural.max} en rural.
              </p>
            </div>
          </div>

          <ColumnChart
            points={daily.data.days.map((day) => ({
              key: day.day,
              label: new Date(`${day.day}T12:00:00`).toLocaleDateString('es-MX', {
                day: '2-digit',
                month: 'short',
              }),
              value: day.total,
              tooltip: `${day.day}: ${fmtInt(day.total)} entrevistas · ${day.byInterviewer
                .map((i) => `${i.code} ${i.completed}`)
                .join(', ')}`,
            }))}
            reference={
              daily.data.days.length > 0
                ? Math.max(
                    ...daily.data.days.map((d) => d.byInterviewer.length),
                  ) * daily.data.expectedPerInterviewerPerDay.urban.min
                : undefined
            }
            referenceLabel="Capacidad esperada del equipo activo"
          />

          <TableView
            columns={['Día', 'Entrevistas', 'Encuestadores activos']}
            rows={daily.data.days.map((day) => [
              day.day,
              fmtInt(day.total),
              fmtInt(day.byInterviewer.length),
            ])}
          />
        </div>
      ) : null}

      {/* --------------------------- Calidad -------------------------------- */}

      <div className="grid grid--2">
        {flags.data && flags.data.length > 0 ? (
          <div className="panel">
            <div className="panel__head">
              <div>
                <h2 className="panel__title">
                  Banderas de calidad <span className="ref">§9.4</span>
                </h2>
                <p className="panel__note">
                  Una bandera no anula la entrevista: la manda a auditoría. La anulación es una
                  decisión humana que además requiere aprobación de un segundo rol.
                </p>
              </div>
            </div>

            <BarChart
              rows={flags.data.map((flag) => ({
                key: flag.flag,
                label: flag.label,
                value: flag.open,
                valueLabel: `${fmtInt(flag.open)} / ${fmtInt(flag.total)}`,
                status:
                  flag.severity === 'critical'
                    ? ('critical' as const)
                    : flag.severity === 'warning'
                      ? ('warning' as const)
                      : undefined,
                tooltip: `${flag.label}: ${fmtInt(flag.open)} sin resolver de ${fmtInt(flag.total)} detectadas · severidad ${flag.severity}`,
              }))}
            />

            <TableView
              columns={['Bandera', 'Severidad', 'Sin resolver', 'Total']}
              rows={flags.data.map((flag) => [
                flag.label,
                flag.severity,
                fmtInt(flag.open),
                fmtInt(flag.total),
              ])}
            />
          </div>
        ) : null}

        {verification.data ? (
          <div className="panel">
            <div className="panel__head">
              <div>
                <h2 className="panel__title">
                  Supervisión <span className="ref">§9.4</span>
                </h2>
                <p className="panel__note">
                  Telefónica del 20% a las 24–48 h y presencial del 10%, sobre{' '}
                  {fmtInt(verification.data.eligibleInterviews)} entrevistas elegibles.
                </p>
              </div>
            </div>

            <div className="stack">
              {verification.data.byMode.map((mode) => (
                <div key={mode.mode}>
                  <div className="row row--between" style={{ marginBottom: 6 }}>
                    <span className="strong" style={{ textTransform: 'capitalize' }}>
                      {mode.mode}
                    </span>
                    <span className="muted num">
                      {fmtInt(mode.completed)} de {fmtInt(mode.targetCount)} ·{' '}
                      {fmtPct(mode.achievedShare, 1)} de {fmtPct(mode.targetShare, 0)}
                    </span>
                  </div>
                  <div className="hbar__track">
                    <span
                      className="hbar__mark"
                      style={{
                        width: `${Math.min(100, mode.targetCount > 0 ? (mode.completed / mode.targetCount) * 100 : 0)}%`,
                      }}
                    />
                  </div>
                  <div className="row row--wrap" style={{ marginTop: 8 }}>
                    {mode.pending > 0 ? (
                      <StatusChip status="warning">{mode.pending} pendientes</StatusChip>
                    ) : null}
                    {mode.outcomes.map((outcome) => (
                      <StatusChip
                        key={outcome.outcome}
                        status={
                          outcome.outcome === 'confirmada'
                            ? 'good'
                            : outcome.outcome === 'falsa' || outcome.outcome === 'inconsistente'
                              ? 'critical'
                              : 'neutral'
                        }
                      >
                        {outcome.outcome}: {outcome.count}
                      </StatusChip>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        ) : null}
      </div>
    </>
  );
}
